import {
  AssetsProvider,
  css,
  mobile,
  styled,
  Text,
  useTranslation,
} from '@obvio/app'
import {
  SvgArea,
  SvgDollar,
  SvgFamily,
  SvgUser,
  SvgUsers,
  SvgUsersMore,
} from '@obvio/svg'
import { RichText } from '@obvio/template'
import { Grid, Stack } from '@obvio/ui'

import { ReadMoreLink } from './ReadMoreLink'
import { CenteredSection } from './Renderer/CenteredSection'
import { HeroSection } from './Renderer/RendererVariants/HeroSection'
import {
  customRenderers,
  getImage,
  MaxWidthGrid,
  MinHeightImage,
  MinHeightImageStyle,
} from './Renderer/utils'
import { useProfitRoom } from './useProfitRoom'

import type { Data } from '@/pages/pokoje/[slug]'
import type { Svg } from '@obvio/svg'
import type { ReactElement } from 'react'

type RoomCategoryPageProps = {
  category: Data
}

const AlignStack = styled(Stack)`
  align-items: center;
`

type WithIconProps = {
  icon: Svg
  text: string
  displayIf: any
}

function WithIcon({ icon: Icon, text, displayIf }: WithIconProps) {
  if (!displayIf) {
    return null
  }
  return (
    <AlignStack spacing="extraSmall">
      <Icon strokeWidth={1} />
      <span>{text}</span>
    </AlignStack>
  )
}

const RoomTitle = styled(Text)`
  font-size: 1.5rem;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${(theme) => theme.stroke};
`

const outerCss = css`
  margin-bottom: 10rem;
`

const TraitsGrid = styled(Grid)`
  @media ${mobile} {
    grid-template-columns: repeat(2, auto);
  }
`

export function RoomCategoryPage({
  category,
}: RoomCategoryPageProps): ReactElement {
  const { bookingOpen } = useProfitRoom()

  const { t } = useTranslation()

  return (
    <>
      <HeroSection type="image" noLogo img={getImage(category.image)} />
      {customRenderers['text-richText-heading-richText-cta']({
        text: 'POKOJE',
        richTextHeading: `<F2>${category.title}<F2>`,
        richText: category.description,
      })}
      {category.elements
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        .map((room) => (
          <AssetsProvider key={room.id} path={`/room/${room.id}`}>
            <CenteredSection outerCss={outerCss}>
              <MaxWidthGrid templateColumns="1.5fr 1fr" gap="medium">
                <Stack kind="vertical" spacing="medium">
                  <MinHeightImageStyle img={room.images[0]} />
                  <RoomTitle tag="h3">{room.title}</RoomTitle>
                  <div>
                    <RichText value={room.description} />
                  </div>
                  <TraitsGrid
                    templateColumns="repeat(4, minmax(0,auto))"
                    gap="extraSmall"
                  >
                    <WithIcon
                      displayIf={room.single}
                      icon={SvgUser}
                      text={t('room.single')}
                    />
                    <WithIcon
                      displayIf={room.double}
                      icon={SvgUsers}
                      text={t('room.double')}
                    />
                    <WithIcon
                      displayIf={room.apartment}
                      icon={SvgUsersMore}
                      text={t('room.apartment')}
                    />
                    <WithIcon
                      displayIf={room.family}
                      icon={SvgFamily}
                      text={t('room.family')}
                    />
                    <WithIcon
                      displayIf={room.size}
                      icon={SvgArea}
                      text={`${room.size ?? ''}m²`}
                    />
                    <WithIcon
                      displayIf={room.priceFrom}
                      icon={SvgDollar}
                      text={t('room.priceFrom', {
                        price: room.priceFrom ?? '',
                      })}
                    />
                  </TraitsGrid>
                  <Divider />
                  {room.profitroomId ? (
                    <ReadMoreLink
                      onClick={() =>
                        bookingOpen?.({
                          RoomId: room.profitroomId as unknown as string,
                        })
                      }
                    >
                      {t('label.booking')}
                    </ReadMoreLink>
                  ) : null}
                </Stack>
                <MinHeightImage img={room.images[1]} />
              </MaxWidthGrid>
            </CenteredSection>
          </AssetsProvider>
        ))}
      {customRenderers['title-richText']({
        title: t('text.yourStay'),
        richText: `<F1>${t('text.roomForYou')}<F1>\n<F2>${t(
          'text.browseAll',
        )}<F2>`,
      })}
      {customRenderers.rooms({})}
    </>
  )
}
